import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  MapIcon,
  BeakerIcon,
  ArrowDownTrayIcon,
  ChartPieIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { farmApi, vraApi, VRAMap, VRAZone } from '../lib/api';
import toast from 'react-hot-toast';
import { INDEX_CONFIG, INDEX_CATEGORIES, getIndexName, COMMON_INDICES } from '../lib/indexConfig';

const MAP_TYPES = [
  { value: 'nitrogen', label: 'Nitrogen (N)', description: 'Variable rate nitrogen application' },
  { value: 'phosphorus', label: 'Phosphorus (P)', description: 'Phosphorus prescription map' },
  { value: 'potassium', label: 'Potassium (K)', description: 'Potassium prescription map' },
  { value: 'seeding', label: 'Seeding Rate', description: 'Variable seeding rate map' },
  { value: 'lime', label: 'Lime Application', description: 'Lime requirement map' },
  { value: 'irrigation', label: 'Irrigation Zones', description: 'Irrigation management zones' },
];

function ZoneCard({ zone }: { zone: VRAZone }) {
  return (
    <div className="bg-white rounded-lg border p-4 flex items-center space-x-4">
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
        style={{ backgroundColor: zone.color }}
      >
        {zone.zone}
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900">{zone.label}</p>
        <p className="text-sm text-gray-500">
          {zone.areaHectares.toFixed(2)} ha ({zone.pixelCount} pixels)
        </p>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-green-600">
          {zone.recommendedRate.toFixed(1)}
        </p>
        <p className="text-xs text-gray-500">{zone.unit}</p>
      </div>
    </div>
  );
}

export default function VRAMaps() {
  const [selectedFarm, setSelectedFarm] = useState<string>('');
  const [mapType, setMapType] = useState<string>('nitrogen');
  const [baseIndex, setBaseIndex] = useState<string>('ndvi');
  const [generatedMap, setGeneratedMap] = useState<VRAMap | null>(null);

  const { data: farms = [], isLoading: farmsLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: cropRates } = useQuery({
    queryKey: ['vra-crop-rates'],
    queryFn: vraApi.getCropRates,
  });

  const generateMutation = useMutation({
    mutationFn: vraApi.generate,
    onSuccess: (response) => {
      setGeneratedMap(response.data);
      toast.success('VRA map generated successfully!');
    },
    onError: () => {
      toast.error('Failed to generate VRA map');
    },
  });

  const exportMutation = useMutation({
    mutationFn: vraApi.exportIsoXml,
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vra_${selectedFarm}_${mapType}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('ISO-XML file downloaded!');
    },
    onError: () => {
      toast.error('Failed to export ISO-XML');
    },
  });

  const handleGenerate = () => {
    const farm = farms.find(f => f.id === selectedFarm);
    if (!farm) {
      toast.error('Please select a farm');
      return;
    }

    generateMutation.mutate({
      farmId: selectedFarm,
      mapType,
      cropType: farm.cropType || 'corn',
      baseIndex,
    });
  };

  const handleExport = () => {
    const farm = farms.find(f => f.id === selectedFarm);
    if (!farm || !generatedMap) return;

    exportMutation.mutate({
      farmId: selectedFarm,
      mapType,
      cropType: farm.cropType || 'corn',
    });
  };

  const pieData = generatedMap?.zones.map(zone => ({
    name: zone.label,
    value: zone.areaHectares,
    color: zone.color,
  })) || [];

  const barData = generatedMap?.zones.map(zone => ({
    name: zone.zone,
    rate: zone.recommendedRate,
    area: zone.areaHectares,
  })) || [];

  if (farmsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">VRA Maps</h1>
          <p className="text-gray-500">Generate Variable Rate Application prescription maps</p>
        </div>
        {generatedMap && (
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Export ISO-XML
          </button>
        )}
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate VRA Map</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Farm Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Farm
            </label>
            <select
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Choose a farm...</option>
              {farms.map(farm => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} ({farm.areaHectares?.toFixed(1)} ha)
                </option>
              ))}
            </select>
          </div>

          {/* Map Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Map Type
            </label>
            <select
              value={mapType}
              onChange={(e) => setMapType(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              {MAP_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {MAP_TYPES.find(t => t.value === mapType)?.description}
            </p>
          </div>

          {/* Base Index Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Index
            </label>
            <select
              value={baseIndex}
              onChange={(e) => setBaseIndex(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <optgroup label="Common Indices">
                {COMMON_INDICES.map(indexId => (
                  <option key={indexId} value={indexId}>
                    {getIndexName(indexId)} - {INDEX_CONFIG[indexId]?.fullName}
                  </option>
                ))}
              </optgroup>
              {INDEX_CATEGORIES.map((category) => (
                <optgroup key={category.name} label={category.name}>
                  {category.indices.filter(id => !COMMON_INDICES.includes(id)).map((indexId) => (
                    <option key={indexId} value={indexId}>
                      {getIndexName(indexId)} - {INDEX_CONFIG[indexId]?.fullName}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {INDEX_CONFIG[baseIndex]?.description}
            </p>
          </div>

          {/* Generate Button */}
          <div className="flex items-end">
            <button
              onClick={handleGenerate}
              disabled={!selectedFarm || generateMutation.isPending}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {generateMutation.isPending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <BeakerIcon className="h-5 w-5 mr-2" />
                  Generate Map
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Supported Crops Info */}
      {cropRates?.data?.supportedCrops && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Supported Crops</p>
            <p className="text-sm text-blue-700">
              {cropRates.data.supportedCrops.join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Generated Map Results */}
      {generatedMap && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <MapIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Area</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {generatedMap.statistics.totalArea.toFixed(1)} ha
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <BeakerIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Average Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {generatedMap.statistics.averageRate.toFixed(1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <ChartPieIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Product</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {generatedMap.statistics.totalProduct.toFixed(0)} kg
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <MapIcon className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Zones</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {generatedMap.zones.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Zone Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => `${value.toFixed(2)} ha`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Rates by Zone</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="rate" name="Rate (kg/ha)" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Zone Details */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Zone Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {generatedMap.zones.map(zone => (
                <ZoneCard key={zone.zone} zone={zone} />
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <p>
              <strong>Crop Type:</strong> {generatedMap.metadata.cropType} |{' '}
              <strong>Base Index:</strong> {generatedMap.metadata.baseIndex.toUpperCase()} |{' '}
              <strong>Generated:</strong> {new Date(generatedMap.generatedAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!generatedMap && (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <MapIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No VRA Map Generated</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Select a farm and map type above, then click "Generate Map" to create a
            variable rate application prescription map.
          </p>
        </div>
      )}
    </div>
  );
}
